import { DateTime } from '@cheqroom/date-time';

import { type DynamicDateFilter, type DynamicFilterValue, getDateQuery, parseDynamicFilters } from '../../filters';

/**
 * Checkout list / search status values accepted by {@link buildCheckoutSearchFilters}.
 */
export enum CheckoutStatusFilter {
	DRAFT = 'DRAFT',
	OPEN = 'OPEN',
	CLOSED = 'CLOSED',
	OVERDUE = 'OVERDUE',
	ARCHIVED = 'ARCHIVED',
}

const statusToListNameMap: Record<string, string> = {
	[CheckoutStatusFilter.DRAFT]: 'creating',
	[CheckoutStatusFilter.OPEN]: 'open',
	[CheckoutStatusFilter.CLOSED]: 'closed',
	[CheckoutStatusFilter.OVERDUE]: 'overdue',
	[CheckoutStatusFilter.ARCHIVED]: 'archived',
};

export type CheckoutListName = [string, 'or' | 'and'];

export type CheckoutSearchFiltersQuery = {
	listNames?: CheckoutListName[];
	fromBetween?: string;
	toBetween?: string;
	started__gte?: string;
	due__gte?: string;
	due__lte?: string;
	location__in?: string[];
	label__in?: string[];
	customer__in?: string[];
	[key: string]: unknown;
};

export type BuildCheckoutSearchFiltersInput = {
	statuses?: string[];
	maintenance?: 'maintenance' | 'no_maintenance';
	dateRange?: {
		start?: string;
		end?: string;
	};
	locationIds?: string[];
	labelIds?: string[];
	customerIds?: string[];
	created?: DynamicDateFilter;
	/** Extra field filters keyed by field id. */
	dynamicFilters?: Record<string, DynamicFilterValue | undefined>;
};

/**
 * Builds Python checkout search / `customFieldFilters` params from neutral filter ids.
 * UI adapters should unwrap chip shapes before calling this.
 */
export const buildCheckoutSearchFilters = (input: BuildCheckoutSearchFiltersInput = {}): CheckoutSearchFiltersQuery => {
	const {
		statuses,
		maintenance,
		dateRange,
		locationIds,
		labelIds,
		customerIds,
		created,
		dynamicFilters = {},
	} = input;

	const listNames: CheckoutListName[] = [];

	const hasStatuses = Boolean(statuses && statuses.length > 0);

	if (!hasStatuses && !maintenance) {
		listNames.push(['unarchived', 'or']);
	}

	if (hasStatuses && statuses) {
		for (const status of statuses) {
			const listName = statusToListNameMap[status];
			if (listName) {
				listNames.push([listName, 'or']);
			}
		}
	}

	if (maintenance === 'maintenance') {
		listNames.push(['maintenance', 'and']);
	}

	if (maintenance === 'no_maintenance') {
		listNames.push(['no_maintenance', 'and']);
	}

	let query: CheckoutSearchFiltersQuery = {
		listNames,
	};

	if (dateRange) {
		const { start, end } = dateRange;

		if (start && end) {
			query.fromBetween = DateTime.fromISOString(start).startOfDay().toFloatingISOString();
			query.toBetween = DateTime.fromISOString(end).endOfDay().toFloatingISOString();
		} else {
			if (start) {
				query.started__gte = DateTime.fromISOString(start).startOfDay().toFloatingISOString();
			}
			if (end) {
				const endDate = DateTime.fromISOString(end);
				query.due__gte = endDate.startOfDay().toFloatingISOString();
				query.due__lte = endDate.endOfDay().toFloatingISOString();
			}
		}
	}

	if (locationIds && locationIds.length > 0) {
		query.location__in = locationIds;
	}

	if (labelIds && labelIds.length > 0) {
		query.label__in = labelIds;
	}

	if (customerIds && customerIds.length > 0) {
		query.customer__in = customerIds;
	}

	if (created) {
		query = { ...query, ...getDateQuery('created', created) };
	}

	return {
		...query,
		...parseDynamicFilters(dynamicFilters),
	};
};
